import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShieldCheck,
  Building2,
  Users,
  Award,
  MapPin,
  Phone,
  Mail,
  Star,
  Clock,
  Sparkles
} from 'lucide-react';
import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { getImageUrl } from '../../utils/imageUtils';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgencyAbout() {
  const { agency } = useOutletContext();
  const { isCustomer, getAuthHeader } = useAuth();
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submitRating = async () => {
    if (!rating) {
      toast.error('Please select a rating');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/agency/${agency.slug}/rate`, {
        rating,
        review
      }, { headers: getAuthHeader() });
      toast.success('Thank you for your rating!');
      setRating(0);
      setReview('');
    } catch (error) {
      toast.error('Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  const hasBusinessInfo = agency.business_hours || agency.business_email || agency.business_phone;
  const hasSpecializations = agency.specializations?.length > 0;

  // Use agency's image or a stock fallback
  const heroImage = agency.about_image
    ? getImageUrl(agency.about_image)
    : 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=2000';

  const aboutImage = agency.about_image
    ? getImageUrl(agency.about_image)
    : 'https://images.unsplash.com/photo-1574621100236-d25b64cfd647?auto=format&fit=crop&q=85&w=1200';

  return (
    <div className="min-h-screen bg-[#FCFDFF]" data-testid="agency-about">
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden bg-navy-900 text-center">
        <div className="absolute inset-0 opacity-20">
          <img
            src={heroImage}
            className="w-full h-full object-cover"
            alt="About Us Background"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl sm:text-7xl font-heading font-bold text-white mb-6">
              About Us
            </h1>
            <p className="text-xl text-navy-200 max-w-2xl mx-auto font-light italic">
              {agency.tagline || `Trusted real estate expertise from ${agency.company_name}.`}
            </p>
          </motion.div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-24 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <span className="text-sky-500 font-bold uppercase tracking-[0.2em] text-xs mb-4 inline-block">
                {agency.company_name}
              </span>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-navy-900 mb-8 leading-tight">
                {agency.tagline ? (
                  <>{agency.tagline.split(' ').slice(0, -1).join(' ')}{' '}
                    <span className="text-sky-500 italic">{agency.tagline.split(' ').slice(-1)[0]}</span>
                  </>
                ) : (
                  <>Architecting Trust in Every <span className="text-sky-500 italic">Square Foot.</span></>
                )}
              </h2>
              <div className="space-y-6">
                <p className="text-navy-600 text-lg leading-relaxed whitespace-pre-line">
                  {agency.about || `${agency.company_name} is a leading real estate agency dedicated to helping clients find their dream properties.`}
                </p>
              </div>

              {/* Specializations */}
              {hasSpecializations && (
                <div className="mt-8 flex flex-wrap gap-2">
                  {agency.specializations.map((spec, i) => (
                    <Badge
                      key={i}
                      variant="secondary"
                      className="bg-sky-50 text-sky-700 border-sky-200 px-4 py-2 text-sm font-medium"
                    >
                      <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                      {spec}
                    </Badge>
                  ))}
                </div>
              )}

              {/* RERA Badge */}
              {agency.rera_number && (
                <div className="mt-12 p-6 glass border border-white shadow-xl rounded-2xl flex items-center gap-6 group hover:scale-[1.02] transition-transform">
                  <div className="w-16 h-16 bg-navy-900 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:bg-sky-600 transition-colors">
                    <ShieldCheck className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-navy-400 font-bold mb-1">Authenticated Partner</p>
                    <p className="font-heading font-bold text-navy-900 text-lg leading-none mb-1">RERA Certified Agency</p>
                    <p className="text-sky-600 font-medium text-sm">{agency.rera_number}</p>
                  </div>
                </div>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9, x: 30 }}
              whileInView={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ duration: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl ring-1 ring-navy-50">
                <img
                  src={aboutImage}
                  alt={`${agency.company_name} showcase`}
                  className="w-full h-[550px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/40 to-transparent" />
              </div>
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-sky-50 rounded-full blur-3xl -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Business Info Section — only shown if agency has this data */}
      {hasBusinessInfo && (
        <section className="py-20 px-6 bg-navy-50/50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy-900 mb-3">Get In Touch</h2>
              <div className="h-1 w-16 bg-sky-500 mx-auto rounded-full" />
            </div>
            <div className="grid sm:grid-cols-3 gap-6">
              {agency.business_hours && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl p-8 border border-navy-100 shadow-sm text-center hover:shadow-lg transition-shadow"
                >
                  <div className="w-14 h-14 bg-sky-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
                    <Clock className="w-7 h-7 text-sky-600" />
                  </div>
                  <h3 className="font-heading font-bold text-navy-900 text-lg mb-2">Business Hours</h3>
                  <p className="text-navy-600">{agency.business_hours}</p>
                </motion.div>
              )}
              {agency.business_email && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl p-8 border border-navy-100 shadow-sm text-center hover:shadow-lg transition-shadow"
                >
                  <div className="w-14 h-14 bg-sky-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
                    <Mail className="w-7 h-7 text-sky-600" />
                  </div>
                  <h3 className="font-heading font-bold text-navy-900 text-lg mb-2">Email Us</h3>
                  <a href={`mailto:${agency.business_email}`} className="text-sky-600 hover:underline font-medium">
                    {agency.business_email}
                  </a>
                </motion.div>
              )}
              {agency.business_phone && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl p-8 border border-navy-100 shadow-sm text-center hover:shadow-lg transition-shadow"
                >
                  <div className="w-14 h-14 bg-sky-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
                    <Phone className="w-7 h-7 text-sky-600" />
                  </div>
                  <h3 className="font-heading font-bold text-navy-900 text-lg mb-2">Call Us</h3>
                  <a href={`tel:${agency.business_phone}`} className="text-sky-600 hover:underline font-medium">
                    {agency.business_phone}
                  </a>
                </motion.div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Philosophy Section */}
      <section className="py-24 px-6 bg-navy-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-sky-500/10 rounded-full blur-[120px] -z-10" />
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">Our Philosophy</h2>
            <div className="h-1 w-20 bg-sky-500 mx-auto rounded-full" />
          </div>
          <div className="grid md:grid-cols-3 gap-12">
            {[
              {
                icon: Award,
                title: 'Unrivaled Quality',
                description: 'We curate only the most exceptional properties that pass our rigorous inspection standards.'
              },
              {
                icon: Users,
                title: 'Personalized Service',
                description: 'Tailored support designed around your unique lifestyle and investment goals.'
              },
              {
                icon: Building2,
                title: 'Market Expertise',
                description: 'Local knowledge powered by deep market intelligence to ensure your investments succeed.'
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group p-8 rounded-3xl border border-white/10 hover:bg-white/5 transition-all"
              >
                <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-sky-500 transition-all">
                  <item.icon className="w-7 h-7 text-sky-400 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-2xl font-heading font-bold mb-4">{item.title}</h3>
                <p className="text-navy-300 leading-relaxed font-light">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ratings Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <span className="text-sky-500 font-bold uppercase tracking-widest text-xs mb-4 inline-block">Client Voice</span>
          <h2 className="text-4xl font-heading font-bold text-navy-900 mb-6">Rate Your Experience</h2>
          <p className="text-navy-500 mb-12 font-light italic">Your feedback shapes our legacy of excellence.</p>

          <div className="glass p-10 rounded-[2.5rem] border border-navy-50 shadow-2xl">
            {isCustomer ? (
              <div className="space-y-8">
                <div className="flex justify-center gap-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setRating(star)}
                      className="transition-all hover:scale-125 hover:rotate-6 focus:outline-none"
                    >
                      <Star
                        className={`w-12 h-12 ${star <= rating ? 'text-yellow-400 fill-yellow-400 drop-shadow-[0_0_15px_rgba(250,204,21,0.4)]' : 'text-navy-100'}`}
                      />
                    </button>
                  ))}
                </div>
                <Textarea
                  placeholder="Share your experience with our team..."
                  value={review}
                  onChange={(e) => setReview(e.target.value)}
                  className="min-h-[120px] bg-white/50 border-navy-50 rounded-2xl p-4 focus:ring-sky-500"
                />
                <Button
                  onClick={submitRating}
                  className="w-full bg-navy-900 hover:bg-navy-800 text-white h-16 rounded-2xl text-lg font-heading"
                  disabled={submitting}
                >
                  {submitting ? 'Sharing...' : 'Share Review'}
                </Button>
              </div>
            ) : (
              <div className="py-6">
                <p className="text-navy-600 mb-6 font-medium">Please login as a customer to provide feedback.</p>
                <Link to="/customer/login">
                  <Button variant="outline" className="h-14 px-10 rounded-2xl border-navy-200 hover:bg-navy-50">
                    Login to Rate
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="relative rounded-[3rem] overflow-hidden bg-navy-900 p-12 lg:p-20 text-center">
            <div className="absolute inset-0 opacity-10">
              <img src={heroImage} className="w-full h-full object-cover" alt="Background" />
            </div>
            <div className="relative z-10">
              <Award className="w-20 h-20 text-sky-400 mx-auto mb-8" />
              <h2 className="text-4xl md:text-6xl font-heading font-bold text-white mb-8">Ready to find your <br /> <span className="text-sky-400 italic">Dream Property?</span></h2>
              <Link to={`/agency/${agency.slug}/contact`}>
                <Button className="bg-sky-500 hover:bg-sky-400 text-white h-16 px-12 rounded-2xl text-xl font-heading shadow-2xl shadow-sky-500/20">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
